/*
  Cart drawer (Dawn 16), extended for a locked layout and fully AJAX updates.

  Dawn's CartItems (assets/cart.js) sends quantity changes and removals to /cart/change and repaints
  the drawer from the Section Rendering API. On top of that, this file:
  - keeps the header cart icon's own markup and only swaps the count bubble (custom headers such as
    cm-header have their own icon and label, which Dawn's full replacement would wipe). The helper,
    cartIconInnerHTML, and CART_ICON_SELECTOR live in assets/cart.js, which loads first;
  - shows a busy state while any cart request is running: the list and footer dim, controls ignore
    clicks, and the checkout button is disabled, so nothing can be double-submitted mid-update;
  - sends a single checkout, even on repeated clicks, and unlocks it when the page comes back from the
    back-forward cache;
  - derives the empty state from the markup after every repaint, whatever added the item;
  - drops a background refresh that finishes after a newer change, so older cart state never repaints
    over newer state;
  - removes a line when its quantity goes below the minimum (the minus button at 1, or typing 0)
    instead of showing a "minimum of 1" error.

  Stage 2 (free delivery bar, suggestions, express checkout, variant swap). The elements themselves are in
  assets/cm-cart-drawer.js; the cart changes they make run here, inside the same busy state, checkout lock
  and stale-refresh guard as the quantity changes:
  - announces the `cart-drawer:open` event, which the suggestions wait for before loading;
  - addItems() adds products from inside the drawer (the "+ Add" suggestions, and quick add on the shop pages)
    with /cart/add.js and repaints the drawer, header count and delivery bar from the returned sections,
    without closing the drawer. Shopify's cart-lines event is resolved with the whole cart from /cart.json,
    because the add response has no currency; the event can never break the add;
  - swapVariant() changes a line's variant: it adds the new variant with the line's quantity, properties
    and selling plan (Shopify merges it into a matching line), then removes the old line;
  - after every change made in the drawer, focus stays on the changed control (or the one that took its
    place) and #CartDrawer-Announce tells screen readers what changed, with the new total and delivery status;
  - the item list keeps its scroll position across repaints;
  - Tab from checkout reaches the express checkout buttons (Shopify renders them in a closed shadow root that
    Dawn's focus trap can't see), and focus comes back to the top of the drawer after the last one.
*/

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Counts running cart requests; the drawer is busy while it is above zero.
    this.pendingRequests = 0;
    // Goes up whenever the cart is changed from here, so a slower background refresh can tell it is stale.
    this.cartVersion = 0;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('keydown', this.onQuantityEnter.bind(this));
    this.addEventListener('keydown', this.onTabToExpressCheckout.bind(this));
    document.addEventListener('focusin', this.onFocusAfterExpressCheckout.bind(this));
    // Coming back from checkout with the back button restores the page as it was left: unlock checkout.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.resetCheckout();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.setNoteAccessibility();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();

    // The suggestions (cm-cart-upsells) load on this, so they never hold up the page or the drawer.
    this.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setNoteAccessibility() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Called by Dawn's product form (and quick add) after /cart/add with the rendered sections.
  renderContents(parsedState) {
    this.cartVersion++;
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections && parsedState.sections[section.id];
      if (!html) return;

      if (section.id === 'cart-icon-bubble') {
        this.updateCartIcon(html);
        return;
      }
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });
    this.syncState();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  /**
   * Repaints the drawer from rendered sections without opening it (used for background refreshes).
   * @param {Object<string, string>} sections - Rendered sections keyed by section name.
   */
  renderSections(sections) {
    const inner = this.querySelector('.drawer__inner');
    if (sections['cart-drawer'] && inner) {
      const scrollTop = this.querySelector('cart-drawer-items')?.scrollTop || 0;
      inner.innerHTML = this.getSectionInnerHTML(sections['cart-drawer'], '.drawer__inner');
      this.restoreScroll(scrollTop);
    }
    if (sections['cart-icon-bubble']) this.updateCartIcon(sections['cart-icon-bubble']);
    this.syncState();
  }

  // A repaint creates a new item list, scrolled to the top; put it back where the shopper was.
  restoreScroll(scrollTop) {
    const list = this.querySelector('cart-drawer-items');
    if (list && scrollTop) list.scrollTop = scrollTop;
  }

  updateCartIcon(sectionHTML) {
    const html = cartIconInnerHTML(sectionHTML);
    if (html !== null) document.getElementById('cart-icon-bubble').innerHTML = html;
  }

  // Brings the drawer's own state in line with freshly rendered markup.
  syncState() {
    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
    this.setNoteAccessibility();
    if (this.pendingRequests > 0) this.setBusy(true);
  }

  // ---------- busy state ----------

  startRequest() {
    this.pendingRequests++;
    this.setBusy(true);
  }

  endRequest() {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    if (this.pendingRequests === 0) this.setBusy(false);
  }

  /**
   * Dims the list and footer, blocks their controls (CSS) and disables checkout while cart requests run.
   * Checkout is re-enabled only if it was disabled here, so an empty cart keeps its disabled button.
   * @param {boolean} busy
   */
  setBusy(busy) {
    this.classList.toggle('is-busy', busy);
    const inner = this.querySelector('.drawer__inner');
    if (inner) busy ? inner.setAttribute('aria-busy', 'true') : inner.removeAttribute('aria-busy');

    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (!checkout) return;
    if (busy && !checkout.disabled) {
      checkout.disabled = true;
      checkout.dataset.busyDisabled = 'true';
    } else if (!busy && checkout.dataset.busyDisabled) {
      checkout.disabled = false;
      delete checkout.dataset.busyDisabled;
    }
  }

  // ---------- changes made from inside the drawer ----------

  /**
   * Posts JSON to the AJAX Cart API.
   * @param {string} url
   * @param {Object} body
   * @returns {Promise<Object>} The response; rejects with the Cart API's message for a refused change.
   */
  postCart(url, body) {
    return fetch(url, { ...fetchConfig('javascript'), body: JSON.stringify(body) })
      .then((response) => response.json().catch(() => ({ status: response.status })))
      .then((response) => {
        if (response.status && response.status >= 400) {
          throw Object.assign(new Error(response.description || response.message || window.cartStrings.error), {
            code: 'INVALID',
          });
        }
        return response;
      });
  }

  /**
   * Adds products from inside the drawer (the suggestions' "+ Add") and repaints the drawer, the header
   * count and the delivery bar, keeping the drawer open.
   * @param {Array<{id: number, quantity: number}>} items
   * @param {{message?: string, focus?: () => (Element|null)}} options - What to announce, and what to
   *   focus after the repaint (the added line when it returns nothing).
   * @returns {Promise<Object>|null} The /cart/add.js response, or null when another change is running.
   */
  addItems(items, { message, focus } = {}) {
    if (this.pendingRequests > 0 || this.checkingOut) return null;
    this.cartVersion++;
    this.startRequest();
    const linesUpdate = this.createCartLinesUpdateEvent(
      'add',
      items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }))
    );

    const request = this.postCart(`${routes.cart_add_url}.js`, {
      items,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    })
      .then((response) => {
        this.renderSections(response.sections);
        this.resolveLinesUpdate(linesUpdate);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: response });
        return response;
      })
      .finally(() => this.endRequest())
      .then((response) => {
        const added = this.querySelector(`[data-quantity-variant-id="${items[0].id}"]`);
        this.focusAfterChange(focus?.() || added?.closest('.cart-item')?.querySelector('.cart-item__name'));
        this.announce(message);
        return response;
      });
    request.catch((error) => {
      linesUpdate?.reject(error);
      this.dispatchCartError(error);
    });
    return request;
  }

  /**
   * Changes a line to another variant of the same product. The Cart API has no call for this, so the new
   * variant is added first, with the line's quantity, properties and selling plan (Shopify merges it into
   * a line with the same variant, properties and plan), and the old line is removed after. If the add is
   * refused (sold out, not enough stock), the line stays as it was and shows why.
   * @param {Object} change
   * @param {number} change.line - The line's 1-based position.
   * @param {string} change.key - The line's key, used to remove it.
   * @param {number} change.from - The current variant ID.
   * @param {number} change.to - The chosen variant ID.
   * @param {number} change.quantity
   * @param {Object} change.properties
   * @param {number|null} change.sellingPlan
   * @param {string} change.message - Announced once the change is made.
   * @param {HTMLSelectElement} change.select
   */
  swapVariant({ line, key, from, to, quantity, properties, sellingPlan, message, select }) {
    const items = this.querySelector('cart-drawer-items');
    if (!items || this.pendingRequests > 0 || this.checkingOut) {
      select.value = String(from);
      return;
    }
    const lineError = this.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text`);
    if (lineError) lineError.textContent = '';
    items.enableLoading(line); // line spinner, busy state and cartVersion++, as for a quantity change

    const item = { id: to, quantity, properties };
    if (sellingPlan) item.selling_plan = sellingPlan;
    let added = false;

    this.postCart(`${routes.cart_add_url}.js`, { items: [item] })
      .then(() => {
        added = true;
        return this.postCart(`${routes.cart_change_url}.js`, {
          id: key,
          quantity: 0,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });
      })
      .then((cart) => {
        this.renderSections(cart.sections);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
        return true;
      })
      .catch((error) => {
        this.dispatchCartError(error);
        // The new variant went in but the old line could not be removed: show the cart as it now is.
        if (added) return this.refresh().then(() => false);
        select.value = String(from);
        if (lineError) lineError.textContent = error.message;
        return false;
      })
      .finally(() => items.disableLoading(line))
      .then((swapped) => {
        const changedSelect = this.querySelector(`cm-variant-select[data-variant-id="${to}"] select`);
        this.focusAfterChange(swapped ? changedSelect : select.isConnected ? select : changedSelect);
        if (swapped) this.announce(message);
      });
  }

  // Repaints the drawer from the current cart (after a change that only partly went through).
  refresh() {
    this.cartVersion++;
    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => this.renderSections(sections))
      .catch((e) => console.error(e));
  }

  // Moves focus to the changed control after a repaint, and resets Dawn's focus trap around the new markup.
  focusAfterChange(element) {
    if (!this.classList.contains('active')) return;
    trapFocus(this, element || this.querySelector('.drawer__inner'));
  }

  /**
   * Tells screen readers what changed, followed by the new estimated total and delivery status. The live
   * region is re-rendered with every repaint, so the text goes in after a short pause, once the new
   * region is in place.
   * @param {string} message
   */
  announce(message) {
    clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      const region = this.querySelector('#CartDrawer-Announce');
      if (!region || !this.classList.contains('active')) return;
      region.textContent = [message, this.cartSummary()].filter(Boolean).join(' ');
    }, 150);
  }

  cartSummary() {
    if (this.classList.contains('is-empty')) return 'Your cart is empty.';
    const total = this.querySelector('.totals__total-value')?.textContent.trim();
    const deliveryMessage = this.querySelector('#CartDrawer-DeliveryMessage');
    let delivery = '';
    if (deliveryMessage) {
      const text = deliveryMessage.cloneNode(true);
      text.querySelectorAll('[aria-hidden="true"]').forEach((node) => node.remove());
      delivery = text.textContent.replace(/\s+/g, ' ').trim();
    }
    return [total && `Estimated total ${total}.`, delivery].filter(Boolean).join(' ');
  }

  createCartLinesUpdateEvent(action, lines) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return null;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'dialog', lines, promise: deferred.promise }));
    return {
      // A cart Shopify's event can't read rejects the event; it never breaks the change the shopper made.
      resolve: ({ cart }) => {
        try {
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        } catch (error) {
          deferred.reject(error);
        }
      },
      reject: (error) => deferred.reject(error),
    };
  }

  // /cart/add.js answers with the added lines only, but the event needs the whole cart (it formats prices in the
  // cart's currency), so fetch it, as Dawn's product form does.
  resolveLinesUpdate(linesUpdate) {
    if (!linesUpdate) return;
    CartItems.fetchCartData()
      .then((cart) => {
        if (!cart?.currency) throw new Error('Missing currency in cart response');
        linesUpdate.resolve({ cart });
      })
      .catch((error) => linesUpdate.reject(error));
  }

  dispatchCartError(error) {
    if (error.code !== 'INVALID') console.error(error); // a refused change (sold out, stock) is not a fault
    const { CartErrorEvent } = window.StandardEvents || {};
    if (CartErrorEvent) {
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: error.code || 'SERVICE_UNAVAILABLE' }));
    }
  }

  // ---------- express checkout and the focus trap ----------

  // Dawn's trap treats checkout as the last stop and sends Tab back to the top; let Tab carry on into the
  // express checkout buttons below it instead.
  onTabToExpressCheckout(event) {
    if (event.key !== 'Tab' || event.shiftKey || event.target.id !== 'CartDrawer-Checkout') return;
    const express = this.querySelector('.cm-express');
    if (express && express.getBoundingClientRect().height > 0) event.stopPropagation();
  }

  // Tab from the last express checkout button leaves the drawer: bring focus back to its top.
  onFocusAfterExpressCheckout(event) {
    const fromExpress = this.lastFocused?.closest?.('.cm-express');
    this.lastFocused = event.target;
    if (!fromExpress || !this.classList.contains('active') || this.contains(event.target)) return;
    const first = getFocusableElements(this.querySelector('.drawer__inner'))[0];
    if (first) first.focus();
  }

  // ---------- checkout ----------

  onSubmit(event) {
    if (event.target.id !== 'CartDrawer-Form') return;
    if (this.pendingRequests > 0 || this.checkingOut) {
      event.preventDefault();
      return;
    }
    // Lock without `disabled`: a disabled submitter is left out of the form data, and Shopify needs
    // the `checkout` field to send the shopper to checkout rather than back to /cart.
    this.checkingOut = true;
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) {
      checkout.setAttribute('aria-disabled', 'true');
      checkout.classList.add('is-loading');
    }
  }

  resetCheckout() {
    this.checkingOut = false;
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (!checkout) return;
    checkout.removeAttribute('aria-disabled');
    checkout.classList.remove('is-loading');
  }

  // Enter in a quantity field would submit the form through its default button, which is checkout.
  // Commit the quantity instead: leaving the field fires its change event once, as a click away would.
  onQuantityEnter(event) {
    if (event.key !== 'Enter' || !event.target.classList.contains('quantity__input')) return;
    event.preventDefault();
    event.target.blur();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // Only quantity boxes change quantities (the variant dropdowns handle their own changes).
  onChange(event) {
    if (!event.target.classList.contains('quantity__input')) return;
    super.onChange(event);
  }

  // Notes what is changing, so the result can be announced and the list kept where it was.
  updateQuantity(line, quantity, event, name, variantId) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.pendingChange = {
        line,
        quantity,
        title: this.querySelector(`#CartDrawer-Item-${line} .cart-item__name`)?.textContent.trim(),
        region: drawer.querySelector('#CartDrawer-Announce'),
        scrollTop: this.scrollTop,
      };
    }
    super.updateQuantity(line, quantity, event, name, variantId);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: CART_ICON_SELECTOR,
      },
    ];
  }

  enableLoading(line) {
    super.enableLoading(line);
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.cartVersion++;
    drawer.startRequest();
  }

  // Runs after Dawn has repainted the drawer: bring the drawer's own state in line, then end the busy state.
  disableLoading(line) {
    super.disableLoading(line);
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.syncState();
    drawer.endRequest();

    // A quantity change or removal that repainted the drawer (a new live region): announce it.
    const change = drawer.pendingChange;
    drawer.pendingChange = null;
    if (!change || change.region === drawer.querySelector('#CartDrawer-Announce')) return;
    drawer.restoreScroll(change.scrollTop);
    const quantity = drawer.querySelector(`#Drawer-quantity-${change.line}`)?.value;
    drawer.announce(
      change.quantity === 0 || !quantity
        ? `Removed ${change.title} from your cart.`
        : `${change.title}: quantity ${quantity}.`
    );
  }

  // Going below the minimum (the minus button at 1, or typing 0) removes the line.
  validateQuantity(event) {
    const input = event.target;
    const value = parseInt(input.value, 10);
    const min = parseInt(input.dataset.min, 10) || 1;
    const previous = parseInt(input.getAttribute('value'), 10);

    if (value === 0 || (value < min && previous <= min)) {
      input.setCustomValidity('');
      this.updateQuantity(
        input.dataset.index,
        0,
        event,
        document.activeElement.getAttribute('name'),
        input.dataset.quantityVariantId
      );
      return;
    }
    super.validateQuantity(event);
  }

  // The cart changed somewhere else on the page (another section, an app): repaint the whole drawer and
  // the header count in one go, unless a newer change from the drawer has already been painted.
  onCartUpdate() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return super.onCartUpdate();
    const startedAt = drawer.cartVersion;

    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => {
        if (drawer.cartVersion !== startedAt) return;
        drawer.renderSections(sections);
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
